<template>
  <div id="app">
    <router-view :auth="auth" :authenticated="authenticated"></router-view>
    <JoinCircle :circle="joinCircle.circle" :user="joinCircle.user" />
    <button hidden id="rootJoinCircleBtn" data-toggle="modal" data-target="#joinCircleModal"></button>
  </div>
</template>

<script>
  import AuthService from './services/auth';
  import DataHandlerService from './services/data';
  import CircleService from "./services/circle";
  import { mapActions, mapMutations } from "vuex";
  import config from "./config";
  import * as Ably from "ably";
  import InMemoryDb from "@/services/inmemorydb";
  import JoinCircle from "@/components/Main/JoinCircle/JoinCircle.vue";

  const auth = new AuthService();
  const data = new DataHandlerService();
  const circleService = new CircleService();
  const InMemoryDatabase = new InMemoryDb();

  export default {
    name: 'App',
    data () {
      return {
        auth,
        authenticated: null,
        ably: {
          client: null,
          channel: null,
          options: {
            key: config.ABLY_API_KEY
          }
        },
        joinCircle: {
          user: null,
          circle: null
        }
      }
    },
    components: {
      JoinCircle
    },
    created() {
      this.authenticated = auth.authenticated;

      auth.authNotifier.on('authChange', payload => {
        this.authenticated = payload.authenticated;
      });

      auth.authNotifier.on('loadCirclesAfterAuth', payload => {
        this.initializeCircleState();
        this.fetchCircles(payload.profile_id);
      });

      auth.alertNotifier.on('error', payload => {
        this.$root.$emit("ShowErrorNotification", payload.message);
      });

      if (auth.authenticated) {
        this.initializeCircleState();
        this.fetchCircles(data.getUserData()._id);
      }

      this.$root.$on("ShowErrorNotification", message => {
        if (message.includes('undefined') || message.includes('not a function')) {
          message = "An error occurred. Please try again."
        }

        setTimeout(() => {
          const notificationExists = Array.from(document.getElementsByClassName("vn-message"))
            .find(s => s.innerHTML.toLowerCase() === message.toLowerCase());
          if (!notificationExists) {
            this.$notification.error(message);
          }
        }, 3000);
      });

      this.setupAbly();

      circleService.getRecommendedCircles().then(circles => {
        this.$root.$emit("RecommendedCirclesRefreshed", circles);
        InMemoryDatabase.saveRecommendedCircles(circles);
      }).catch(() => {});

      this.$root.$on("JoinRecommendedCircle", obj => {
        this.joinCircle = obj;
        document.getElementById('rootJoinCircleBtn').click();
      });
    },
    methods: {
      ...mapActions(["fetchCircles", "updateCircle", "removeCircle"]),
      ...mapMutations(["initializeCircleState"]),
      setupAbly() {
        this.ably.client = new Ably.Realtime(this.ably.options);
        this.ably.client.connection.on("connected", () => {
          this.ably.channel = this.ably.client.channels.get("InfodemicsWebUI");

          this.$root.$on("SaveNewPost", post => {
            this.ably.channel.publish("SaveNewPost", post);
          });

          this.$root.$on("SaveNewTopic", res => {
            this.ably.channel.publish("SaveNewTopic", res);
          });

          this.$root.$on("SaveNewCircleLocation", res => {
            this.ably.channel.publish("SaveNewCircleLocation", res);
          });

          this.$root.$on("RemoveCircleLocation", res => {
            this.ably.channel.publish("RemoveCircleLocation", res);
          });

          this.$root.$on("SaveModifiedCircleLocation", res => {
            this.ably.channel.publish("SaveModifiedCircleLocation", res);
          });

          this.$root.$on("SaveModifiedCircle", res => {
            this.ably.channel.publish("SaveModifiedCircle", res);
          });

          this.$root.$on("SaveModifiedCircleFlag", res => {
            this.ably.channel.publish("SaveModifiedCircleFlag", res);
          });

          this.$root.$on("SaveModifiedPost", res => {
            this.ably.channel.publish("SaveModifiedPost", res);
          });

          this.$root.$on("SaveModifiedCircleIcon", res => {
            this.ably.channel.publish("SaveModifiedCircleIcon", res);
          });

          this.$root.$on("RemoveFlagFromPost", res => {
            this.ably.channel.publish("RemoveFlagFromPost", res);
          });

          this.$root.$on("RemoveCircleRequirement", res => {
            this.ably.channel.publish("RemoveCircleRequirement", res);
          });

          this.$root.$on("AddCircleRequirement", res => {
            this.ably.channel.publish("AddCircleRequirement", res);
          });

          this.$root.$on("ChangeMemberRole", res => {
            this.ably.channel.publish("ChangeMemberRole", res);
          });

          this.$root.$on("RemoveMemberFromCircle", res => {
            this.ably.channel.publish("RemoveMemberFromCircle", res);
          });

          this.$root.$on("AddMemberToCircle", res => {
            this.ably.channel.publish("AddMemberToCircle", res);
          });

          this.$root.$on("DeleteCircle", res => {
            this.ably.channel.publish("DeleteCircle", res);
          });

          this.$root.$on("SaveNewChildCircle", res => {
            this.ably.channel.publish("SaveNewChildCircle", res);
          });

          this.$root.$on("CircleModified", circle => this.updateCircle(circle));
          this.$root.$on("CircleDeleted", _id => this.removeCircle(_id));

          this.$root.$on("SaveModifiedProfile", profile => data.setUserData(profile));

          this.ably.channel.subscribe("SaveNewPost", async (msg) => {
            const post = msg.data;
            const circle = await InMemoryDatabase.getCircle(post.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              InMemoryDatabase.addPost(post);
              this.$root.$emit("NewPostAdded", post);
            }
          });

          this.ably.channel.subscribe("SaveNewTopic", async (msg) => {
            const { topic, post } = msg.data;
            const circle = await InMemoryDatabase.getCircle(post.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              InMemoryDatabase.addPost(post);
              InMemoryDatabase.addTopic(topic);
              this.$root.$emit("NewPostAdded", post);
              this.$root.$emit("NewTopicAdded", topic);
            }
          });

          this.ably.channel.subscribe("SaveNewCircleLocation", async (msg) => {
            const location = msg.data;
            const circle = await InMemoryDatabase.getCircle(location.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              circle.locations = [...(circle.locations || []), location];
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("RemoveCircleLocation", async (msg) => {
            const location = msg.data;
            const circle = await InMemoryDatabase.getCircle(location.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              circle.locations = circle.locations.filter(s => s._id !== location._id);
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("SaveModifiedCircleLocation", async (msg) => {
            const location = msg.data;
            const circle = await InMemoryDatabase.getCircle(location.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              const locationIndex = circle.locations.findIndex(s => s._id === location._id);
              if (locationIndex > -1) {
                circle.locations.splice(locationIndex, 1, location);
                InMemoryDatabase.addCircle(circle)
                this.$root.$emit("CircleModified", circle);
              }
            }
          });

          this.ably.channel.subscribe("SaveModifiedCircle", async (msg) => {
            const _circle = msg.data;
            const circle = await InMemoryDatabase.getCircle(_circle._id);
            if (circle && Object.keys(circle).length > 0) {
              circle.title = _circle.title;
              circle.upload_id = _circle.upload_id;
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("SaveModifiedCircleFlag", async (msg) => {
            const flag = msg.data;
            const circle = await InMemoryDatabase.getCircle(flag.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              const flagIndex = circle.flags.findIndex(s => s._id === flag._id);
              if (flagIndex > -1) {
                circle.flags.splice(flagIndex, 1, flag);
                InMemoryDatabase.addCircle(circle)
                this.$root.$emit("CircleModified", circle);
              }
            }
          });

          this.ably.channel.subscribe("SaveModifiedPost", async (msg) => {
            const flag = msg.data;
            const post = await InMemoryDatabase.getPost(flag.media_id);
            if (post && Object.keys(post).length > 0) {
              const circle = await InMemoryDatabase.getCircle(post.circle_id);
              if (circle && Object.keys(circle).length > 0) {
                const flagIndex = post.media_flags.findIndex(s => s._id === flag._id);
                if (flagIndex > -1) {
                  post.media_flags.splice(flagIndex, 1, flag);
                } else {
                  post.media_flags.push(flag);
                }
                InMemoryDatabase.addPost(post)
                this.$root.$emit("PostModified", post);
              }
            }
          });

          this.ably.channel.subscribe("RemoveFlagFromPost", async (msg) => {
            const flag = msg.data;
            const post = await InMemoryDatabase.getPost(flag.media_id);
            if (post && Object.keys(post).length > 0) {
              const circle = await InMemoryDatabase.getCircle(post.circle_id);
              if (circle && Object.keys(circle).length > 0) {
                post.media_flags = post.media_flags.filter(s => s._id !== flag._id);
                InMemoryDatabase.addPost(post)
                this.$root.$emit("PostModified", post);
              }
            }
          });

          this.ably.channel.subscribe("SaveModifiedCircleIcon", async (msg) => {
            const _circle = msg.data;
            const circle = await InMemoryDatabase.getCircle(_circle._id);
            if (circle && Object.keys(circle).length > 0) {
              circle.upload_id = _circle.upload_id;
              circle.upload = _circle.upload;
              circle.icon = _circle.icon;

              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("RemoveCircleRequirement", async (msg) => {
            const identity = msg.data;
            const circle = await InMemoryDatabase.getCircle(identity.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              circle.identities = circle.identities.filter(s => s._id !== identity._id);
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("AddCircleRequirement", async (msg) => {
            const identity = msg.data;
            const circle = await InMemoryDatabase.getCircle(identity.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              circle.identities.push(identity);
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("ChangeMemberRole", async (msg) => {
            const member = msg.data;
            const circle = await InMemoryDatabase.getCircle(member.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              const board = circle.board || circle.boards || [];
              board.splice(board.findIndex(b => b._id === member._id), 1, member);
              if (circle.boards) {
                circle.boards = board;
              }
              if (circle.board) {
                circle.board = board;
              }
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("RemoveMemberFromCircle", async (msg) => {
            const member = msg.data;
            const circle = await InMemoryDatabase.getCircle(member.circle_id);
            if (circle && Object.keys(circle).length > 0) {
              if (circle.boards) {
                circle.boards = circle.boards.filter(s => s._id !== member._id);
              }
              if (circle.board) {
                circle.board = circle.board.filter(s => s._id !== member._id);
              }
              circle.member_count -= 1;
              InMemoryDatabase.addCircle(circle)
              this.$root.$emit("CircleModified", circle);
            }
          });

          this.ably.channel.subscribe("AddMemberToCircle", async (msg) => {
            const circle = msg.data;
            const user = data.getUserData();

            if (circle.boards) {
              if (circle.boards.find(s => s.profile_id === user._id)) {
                InMemoryDatabase.addCircle(circle)
                this.$root.$emit("CircleModified", circle);
              }
            }

            if (circle.board) {
              if (circle.board.find(s => s.profile_id === user._id)) {
                InMemoryDatabase.addCircle(circle)
                this.$root.$emit("CircleModified", circle);
              }
            }
          });

          this.ably.channel.subscribe("DeleteCircle", async (msg) => {
            const circle = msg.data;
            const circleType  = circle.flags.find(s => s.type === "type").value;

            if (circleType !== "child") {
              InMemoryDatabase.removeCircle(circle._id);
              this.$root.$emit("CircleDeleted", circle._id);
            } else {
              const parentCircle = await InMemoryDatabase.removeChildCircle(circle._id);
              this.$root.$emit("CircleModified", parentCircle)
            }
          });

          this.ably.channel.subscribe("SaveNewChildCircle", async (msg) => {
            const childCircle = msg.data.circle;
            const chain = msg.data.chain;

            await InMemoryDatabase.addCircle(childCircle);

            const parentCircle = await InMemoryDatabase.getCircle(chain.parent);
            if (parentCircle && Object.keys(parentCircle).length > 0) {
              parentCircle.children.push(chain);
              InMemoryDatabase.addCircle(parentCircle)
              this.$root.$emit("CircleModified", parentCircle);
            }
          });
        });

        this.ably.client.connection.on("failed", () => {
          this.$root.$emit("ShowErrorNotification", "Failed to connect to realtime data stream");
        });
      }
    }
  }
</script>

<style lang="scss" src="./App.scss"></style>
